import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';

const RATINGS = [1, 2, 3, 4, 5];
const LABEL = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent' };

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {RATINGS.map(r => (
        <button
          key={r}
          type="button"
          onClick={() => onChange(r)}
          onMouseEnter={() => setHover(r)}
          onMouseLeave={() => setHover(0)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            fontSize: 28, color: r <= (hover || value) ? '#C07000' : '#D1D5DB',
            transition: 'color 0.1s',
          }}
        >★</button>
      ))}
      {value > 0 && (
        <span style={{ fontSize: 13, color: '#6B7280', marginLeft: 4 }}>{LABEL[value]}</span>
      )}
    </div>
  );
}

export default function SurveyPage() {
  const [status, setStatus] = useState('loading'); // loading | valid | expired | submitted | done | error
  const [tokenData, setTokenData] = useState(null);
  const [ratings, setRatings] = useState({ performance: 0, customerService: 0, knowledge: 0 });
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Extract token from URL
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    loadToken();
  }, [token]); // eslint-disable-line

  async function loadToken() {
    try {
      const { data, error } = await supabase
        .from('survey_tokens')
        .select('*')
        .eq('token', token)
        .single();

      if (error || !data) { setStatus('error'); return; }
      if (data.submitted_at) { setStatus('submitted'); return; }
      if (new Date(data.expires_at) < new Date()) { setStatus('expired'); return; }

      setTokenData(data);
      setStatus('valid');
    } catch (e) {
      setStatus('error');
    }
  }

  async function handleSubmit() {
    if (ratings.performance === 0 || ratings.customerService === 0 || ratings.knowledge === 0) {
      alert('Please rate all three categories before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('survey_tokens')
        .update({
          submitted_at: new Date().toISOString(),
          response: { ratings, feedback, submittedAt: new Date().toISOString() },
        })
        .eq('token', token);

      if (error) throw error;
      setStatus('done');
    } catch (e) {
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const containerStyle = {
    minHeight: '100vh', background: '#F9FAFB', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '24px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  const cardStyle = {
    background: '#fff', borderRadius: 16, padding: '40px 48px',
    maxWidth: 560, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  };

  if (status === 'loading') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <p style={{ textAlign: 'center', color: '#6B7280' }}>Loading survey…</p>
      </div>
    </div>
  );

  if (status === 'expired') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Survey Link Expired</h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6 }}>This survey link has expired. Survey links are valid for 14 days. Please contact your contract analyst if you need a new link.</p>
        </div>
      </div>
    </div>
  );

  if (status === 'submitted') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Already Submitted</h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6 }}>This survey has already been submitted. Thank you for your feedback.</p>
        </div>
      </div>
    </div>
  );

  if (status === 'done') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Thank You!</h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6 }}>Your feedback has been submitted successfully. Your response will be shared with the analyst's supervisor.</p>
        </div>
      </div>
    </div>
  );

  if (status === 'error') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Invalid Survey Link</h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6 }}>This survey link is invalid or has already been used. Please contact your contract analyst for assistance.</p>
        </div>
      </div>
    </div>
  );

  const isInternal = tokenData?.recipient_type === 'internal';

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, background: '#0F172A', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>
            GS
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Contract Performance Survey</h1>
          <p style={{ fontSize: 14, color: '#6B7280' }}>
            {isInternal ? 'Internal Stakeholder Survey' : 'Contractor Survey'} · {tokenData?.contract_title}
          </p>
        </div>

        <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.6, marginBottom: 28, padding: '12px 16px', background: '#F3F4F6', borderRadius: 10 }}>
          Please rate the performance of <strong>{tokenData?.analyst_name || 'your contract analyst'}</strong> for contract <strong>{tokenData?.contract_title}</strong>. Your feedback is confidential and will be shared with their supervisor.
        </p>

        {/* Ratings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 28 }}>
          {[
            { key: 'performance', label: 'Overall Performance', desc: 'How well did the analyst manage the contract process?' },
            { key: 'customerService', label: 'Customer Service', desc: 'Were they responsive, professional, and helpful?' },
            { key: 'knowledge', label: 'Knowledge & Expertise', desc: 'Did they demonstrate strong contracting knowledge?' },
          ].map(({ key, label, desc }) => (
            <div key={key}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 8 }}>{desc}</div>
              <StarRating value={ratings[key]} onChange={v => setRatings(p => ({ ...p, [key]: v }))} />
            </div>
          ))}
        </div>

        {/* Additional feedback */}
        <div style={{ marginBottom: 28 }}>
          <label style={{ fontWeight: 600, fontSize: 14, color: '#111827', display: 'block', marginBottom: 6 }}>
            Additional Feedback <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span>
          </label>
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            rows={4}
            placeholder="Any additional comments, suggestions, or feedback…"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 14,
              border: '1.5px solid #E5E7EB', resize: 'vertical', fontFamily: 'inherit',
              boxSizing: 'border-box', outline: 'none',
            }}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || ratings.performance === 0 || ratings.customerService === 0 || ratings.knowledge === 0}
          style={{
            width: '100%', padding: '14px', borderRadius: 10, border: 'none',
            background: (submitting || ratings.performance === 0) ? '#D1D5DB' : '#C07000',
            color: '#fff', fontWeight: 700, fontSize: 15, cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
        >
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </button>

        <p style={{ textAlign: 'center', fontSize: 11.5, color: '#9CA3AF', marginTop: 16 }}>
          This link expires in 14 days and can only be used once. · Powered by GoldenScope
        </p>
      </div>
    </div>
  );
}
